import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import type { OwnedAccount, OwnedAccountDTO } from '@hotjar/account';
import { mapOwnedAccountDtoToModel } from '@hotjar/account';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { SubscriptionCost } from './SubscriptionCost/SubscriptionCost';
import type { SubscriptionInfo } from './SubscriptionNextInvoice/SubscriptionNextInvoice';
import { SubscriptionNextInvoice } from './SubscriptionNextInvoice/SubscriptionNextInvoice';
import { SubscriptionPaymentDetails } from './SubscriptionPaymentDetails/SubscriptionPaymentDetails';
import { LegacySubscriptionPaymentDetails } from './SubscriptionPaymentDetails/LegacySubscriptionPaymentDetails';

interface Props {
  account: OwnedAccountDTO;
  onDeletePayment: () => void;
  onUpdatePayment: (x: string) => void;
  onChangeSubscription: (x: string) => void;
  subscriptionInfo: SubscriptionInfo;
  subscriptionPriceSummary: PriceSummary;
}

const Section = styled.div`
  display: flex;
  gap: ${tokens.spacer.x2};
  justify-content: space-between;
  padding: ${tokens.spacer.x3} 0;
  align-items: stretch;
  min-height: 185px;
`;

export type SubscriptionSummaryAccount = Pick<
  OwnedAccount,
  'billingCycle' | 'hasPaidPlan' | 'lock' | 'currency' | 'features' | 'id' | 'pricingTableId'
>;

export const SubscriptionSummary = ({
  account,
  subscriptionInfo,
  onDeletePayment,
  onUpdatePayment,
  onChangeSubscription,
  subscriptionPriceSummary,
}: Props): JSX.Element | null => {
  // TODO: this conversion is an interim step... use useOwnedAccount instead
  const ownedAccount = mapOwnedAccountDtoToModel(account);
  const zuoraEnabled = isZuoraEnabled(ownedAccount);

  return ownedAccount.billingCycle !== 'yearly' ? (
    <Section>
      <SubscriptionCost
        account={ownedAccount}
        onSubmit={onChangeSubscription}
        hasPaidPlan={ownedAccount.hasPaidPlan}
        subscriptionPriceSummary={subscriptionPriceSummary}
        zuoraEnabled={zuoraEnabled}
      />
      <SubscriptionNextInvoice
        subscriptionInfo={subscriptionInfo}
        hasPaidPlan={ownedAccount.hasPaidPlan}
        zuoraEnabled={zuoraEnabled}
      />
      {zuoraEnabled && <SubscriptionPaymentDetails account={ownedAccount} />}
      {!zuoraEnabled && (
        <LegacySubscriptionPaymentDetails
          account={ownedAccount}
          subscriptionInfo={subscriptionInfo}
          onDeletePayment={onDeletePayment}
          onUpdatePayment={onUpdatePayment}
        />
      )}
    </Section>
  ) : null;
};
